import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";

export function SalonsDrawer({
  title, eyebrow, description, children, onClose, busy = false, width = "regular",
}: {
  title: string;
  eyebrow?: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
  busy?: boolean;
  width?: "regular" | "wide";
}) {
  const titleId = useId();
  const panelRef = useRef<HTMLElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => panelRef.current?.focus());
    return () => {
      window.cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
      restoreFocusRef.current?.focus();
    };
  }, []);

  function handleKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") {
      event.stopPropagation();
      closeRef.current();
      return;
    }
    if (event.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = Array.from(panel.querySelectorAll<HTMLElement>(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )).filter((element) => element.getClientRects().length > 0);
    if (!focusable.length) { event.preventDefault(); panel.focus(); return; }
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus();
    }
  }

  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="cd-salons-shell cd-salons-overlay">
      <div className="cd-salons-drawer-backdrop" aria-hidden="true" onClick={onClose} />
      <aside
        className={"cd-salons-drawer-panel cd-salons-drawer-panel--" + width}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panelRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        aria-busy={busy || undefined}
      >
        <header className="cd-salons-drawer-header">
          <div className="cd-salons-drawer-header__content">
            {eyebrow && <span className="cd-salons-drawer-eyebrow">{eyebrow}</span>}
            <h2 id={titleId}>{title}</h2>
            {description && <p>{description}</p>}
          </div>
          <button type="button" className="cd-salons-drawer-close" aria-label="Cerrar panel" onClick={onClose}>
            <span aria-hidden="true">×</span>
          </button>
        </header>
        <div className="cd-salons-drawer-content">{children}</div>
      </aside>
    </div>,
    document.body,
  );
}
